import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import useSWR from 'swr';

import { IReward } from 'containers/Reward/types';
import { IDetailResponse, IPaginatedResponse } from 'utils/types';

import { fetcher } from './utils';

export interface IRewardBatch {
  id: number;
  name: string;
  currency_code?: string | null;
  const_price?: number | null;
  unit_price?: number | null;
  balance_min?: number | null;
  balance_max?: number | null;
  total?: number | null;
}

export const useReward = (id?: number | string | null) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<IReward>>(
    id ? `/v4/dash/rewards/${id}` : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useRewardWithToken = (id?: number | string | null, token?: string | null) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<IReward>>(
    id && token
      ? {
          url: `/v4/dash/rewards/${id}`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          external: true
        }
      : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useRewardList = (params: Record<string, any> = {}, shouldFetch: boolean = true) => {
  const sanitizedParams = omitBy(params, isNil);
  const key = shouldFetch ? { url: '/v4/dash/rewards', params: sanitizedParams } : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<IReward>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useRewardsWithIds = (ids: number[] = []) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<IReward>>(
    ids.length > 0 ? `/v4/dash/rewards?id=${ids.join(',')}&limit=1000` : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useRewardBatchList = (
  rewardId?: number | string | null,
  params: Record<string, any> = {},
  shouldFetch: boolean = true
) => {
  const key =
    rewardId && shouldFetch ? { url: `/v4/dash/rewards/${rewardId}/batches`, params } : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<IRewardBatch>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};
